import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { isIndianUser } from "@/utils/getCountryCode";
import { parseNotificationCtaPayload } from "@/utils/notificationCta";

dayjs.extend(utc);

/** UserTrackingApi: notification opened / clicked from navbar. */
export const NOTIFICATION_STATUS_CLICKED = "Opened";

/** GetRecentNotifications status: All / Read / Unread. */
export const NOTIFICATION_LIST_STATUS = {
  all: 0,
  read: 1,
  unread: 2,
};

/** Backend country origin for notification APIs (India vs UAE). */
export function getNotificationCountryOrigin() {
  return isIndianUser() ? 1 : 2;
}

export function normalizeNotificationItem(raw = {}) {
  const isRead = Boolean(
    raw.isRead ?? raw.IsRead ?? raw.read ?? raw.Read ?? false
  );

  return {
    id: String(raw.id ?? raw.Id ?? raw.notificationId ?? raw.NotificationId ?? ""),
    title: String(raw.title ?? raw.Title ?? raw.subject ?? raw.Subject ?? "").trim(),
    description: String(
      raw.description ??
        raw.Description ??
        raw.body ??
        raw.Body ??
        raw.message ??
        raw.Message ??
        ""
    ).trim(),
    isRead,
    sentOnUtc:
      raw.sentOnUtc ??
      raw.SentOnUtc ??
      raw.sentOnUTC ??
      raw.SentOnUTC ??
      raw.createdAt ??
      raw.CreatedAt ??
      raw.sentDate ??
      raw.SentDate ??
      raw.date ??
      "",
    ctaAction: Number(raw.ctaAction ?? raw.CtaAction ?? 0) || 0,
    ctaPayload:
      raw.ctaPayload ??
      raw.CtaPayload ??
      raw.ctaData ??
      raw.CtaData ??
      null,
    ...extractNotificationTrackingIds(raw),
  };
}

function extractNotificationTrackingIds(raw = {}) {
  const ctaPayload =
    raw.ctaPayload ?? raw.CtaPayload ?? raw.ctaData ?? raw.CtaData ?? null;
  const payloadData = parseNotificationCtaPayload(ctaPayload);

  return {
    logId:
      Number(raw.logId ?? raw.LogId ?? payloadData.logId ?? payloadData.LogId ?? 0) ||
      0,
    campaignId:
      Number(
        raw.campaignId ??
          raw.CampaignId ??
          payloadData.campaignId ??
          payloadData.CampaignId ??
          0
      ) || 0,
  };
}

function unwrapNotificationPayload(response) {
  if (Array.isArray(response)) return { items: response };
  if (!response || typeof response !== "object") return {};

  const nested = response.data ?? response.Data;
  if (
    nested &&
    typeof nested === "object" &&
    !Array.isArray(nested) &&
    (nested.items ||
      nested.Items ||
      nested.total != null ||
      nested.Total != null ||
      nested.unreadCount != null)
  ) {
    return nested;
  }

  return response;
}

export function normalizeNotificationsResponse(response) {
  const payload = unwrapNotificationPayload(response);
  const list = Array.isArray(payload)
    ? payload
    : payload.items ??
      payload.Items ??
      payload.notifications ??
      payload.Notifications ??
      payload.list ??
      [];

  const items = (Array.isArray(list) ? list : []).map(normalizeNotificationItem);

  const unreadFromApi = Number(
    payload.unreadCount ??
      payload.UnreadCount ??
      response?.unreadCount ??
      response?.UnreadCount
  );

  const unreadCount = Number.isFinite(unreadFromApi)
    ? unreadFromApi
    : items.filter((item) => !item.isRead).length;

  const totalFromApi = Number(
    payload.totalCount ??
      payload.TotalCount ??
      payload.total ??
      payload.Total ??
      payload.count ??
      response?.totalCount ??
      response?.TotalCount ??
      response?.total
  );

  const readFromApi = Number(payload.readCount ?? payload.ReadCount);
  const totalCount = Number.isFinite(totalFromApi) ? totalFromApi : items.length;
  const readCount = Number.isFinite(readFromApi)
    ? readFromApi
    : Math.max(0, totalCount - unreadCount);

  return { items, unreadCount, totalCount, readCount };
}

function parseNotificationUtcToLocal(value) {
  if (!value) return null;

  if (value instanceof Date) {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
  }

  const raw = String(value).trim();
  if (!raw) return null;

  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw);
  const parsed = hasTimezone ? dayjs(raw) : dayjs.utc(raw);
  return parsed.isValid() ? parsed.local() : null;
}

function formatRelativeShort(parsed) {
  const now = dayjs();
  const minutes = Math.max(0, now.diff(parsed, "minute"));

  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} min ago`;

  const hours = now.diff(parsed, "hour");
  if (hours < 24) return hours === 1 ? "1 hour ago" : `${hours} hours ago`;

  const days = now.diff(parsed, "day");
  if (days < 30) return days === 1 ? "1 day ago" : `${days} days ago`;

  const months = now.diff(parsed, "month");
  if (months < 12) return months === 1 ? "1 month ago" : `${months} months ago`;

  const years = Math.max(1, now.diff(parsed, "year"));
  return years === 1 ? "1 year ago" : `${years} years ago`;
}

export function formatNotificationTimestamp(value) {
  const parsed = parseNotificationUtcToLocal(value);
  if (!parsed) return "";

  return `${formatRelativeShort(parsed)}, ${parsed.format("DD/MM/YYYY")}`;
}

export function formatNotificationDateTime(value) {
  const parsed = parseNotificationUtcToLocal(value);
  if (!parsed) return "";

  return parsed.format("DD/MM/YYYY, hh:mm A");
}
